import { CustomerDeliveryAddress } from "../../../data/entities";

export const MAX_CUSTOMER_DELIVERY_ADDRESSES = 5;

export interface DeliveryAddressFieldErrors {
    label?: string;
    address?: string;
}

export const getDeliveryAddressFieldErrors = (
    addresses: CustomerDeliveryAddress[],
): Record<number, DeliveryAddressFieldErrors> => {
    const errors: Record<number, DeliveryAddressFieldErrors> = {};
    const labelIndexes = new Map<string, number[]>();

    addresses.forEach((item, index) => {
        const label = item.label.trim();
        const address = item.address.trim();

        if (!label) {
            errors[index] = {
                ...errors[index],
                label: "Vui lòng nhập tên gợi nhớ.",
            };
        } else {
            const normalizedLabel = label.toLocaleLowerCase("vi-VN");
            labelIndexes.set(normalizedLabel, [
                ...(labelIndexes.get(normalizedLabel) || []),
                index,
            ]);
        }

        if (!address) {
            errors[index] = {
                ...errors[index],
                address: "Vui lòng nhập địa chỉ đầy đủ.",
            };
        }
    });

    labelIndexes.forEach((indexes) => {
        if (indexes.length < 2) return;
        indexes.forEach((index) => {
            errors[index] = {
                ...errors[index],
                label: "Tên gợi nhớ không được trùng nhau.",
            };
        });
    });

    return errors;
};

export const getDeliveryAddressesValidationMessage = (
    addresses: CustomerDeliveryAddress[],
): string | null => {
    if (addresses.length > MAX_CUSTOMER_DELIVERY_ADDRESSES) {
        return `Mỗi khách hàng chỉ được lưu tối đa ${MAX_CUSTOMER_DELIVERY_ADDRESSES} địa chỉ giao hàng.`;
    }

    const fieldErrors = getDeliveryAddressFieldErrors(addresses);
    const firstError = Object.values(fieldErrors)[0];
    return firstError?.label || firstError?.address || getDefaultDeliveryAddressValidationMessage(addresses);
};

export const getDefaultDeliveryAddressValidationMessage = (
    addresses: CustomerDeliveryAddress[],
): string | null =>
    addresses.length > 0 && addresses.filter((item) => item.is_default === true).length !== 1
        ? "Vui lòng chọn đúng một địa chỉ giao hàng mặc định."
        : null;

export const selectDefaultDeliveryAddress = (
    addresses: CustomerDeliveryAddress[],
    index: number,
): CustomerDeliveryAddress[] => {
    if (index < 0 || index >= addresses.length) return addresses;
    return addresses.map((item, itemIndex) => ({
        ...item,
        is_default: itemIndex === index,
    }));
};

export const removeDeliveryAddress = (
    addresses: CustomerDeliveryAddress[],
    index: number,
): CustomerDeliveryAddress[] => {
    const remaining = addresses.filter((_, itemIndex) => itemIndex !== index);
    return addresses[index]?.is_default === true && remaining.length > 0
        ? selectDefaultDeliveryAddress(remaining, 0)
        : remaining;
};

export const serializeDeliveryAddresses = (
    addresses: CustomerDeliveryAddress[],
): CustomerDeliveryAddress[] =>
    addresses.map(({ id, label, address, is_default }) => ({
        ...(id ? { id } : {}),
        label: label.trim(),
        address: address.trim(),
        is_default: is_default === true,
    }));
